import { userApi } from '@/app/redux/endpoints/user';
import { useAppDispatch } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useUser } from '@/app/redux/hooks/user';
import { Invitation } from '@/common/types/organisation';
import InitialsCircle from '@/common/ui/InitialsCircle';
import { DataLoader } from '@/common/ui/LoadingSpinners';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { handleLogout } from '@/common/utils/auth';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import ensureError from '@/common/utils/error';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { respondToInviteThunk } from '@/dashboard/dashboardThunks';
import api from '@/db/apiClient';
import { Button } from '@shadcn/components/button';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import styled from 'styled-components';

const handledTokens = new Set<string>();

export default function InviteHandler() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token } = useParams();

  const { switchOrganisation } = useCurrentOrganisationId();
  const { user } = useUser();

  const [isLoading, setLoading] = useState(true);
  const [invite, setInvite] = useState<Invitation | null>(null);

  useEffect(() => {
    if (token && handledTokens.has(token)) return;
    if (!token) {
      toast.error('Failed to respond to invite', {
        description:
          'Missing invite token. Please check the link and try again.',
      });
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const inviteDetails = await api.asEducator.fetchInviteDetails(token);
        setInvite(inviteDetails);
      } catch (error) {
        console.error('Error fetching invite details:', error);
        const message = (error as Error).message;
        toast.error('Failed to respond to invite', {
          description: message,
        });
        navigate(getDefaultDashboardRoute());
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleInviteResponse(accept: boolean) {
    if (!user) {
      console.error('User is missing');
      toast.error('Failed to respond to invite', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    if (!token || !invite) {
      toast.error('Failed to respond to invite', {
        description:
          'Missing invite token. Please check the link and try again.',
      });
      return;
    }
    setLoading(true);
    handledTokens.add(token);
    try {
      await dispatch(respondToInviteThunk(invite, accept, switchOrganisation));
      trackMixpanelEvent(accept ? 'Invite Accepted' : 'Invite Rejected', {
        organisationId: invite.organisationId,
        referrer: invite.inviterEmail,
      });
      toast.success(accept ? 'Invite accepted' : 'Invite rejected');
      dispatch(
        userApi.util.updateQueryData(
          'getPendingInvite',
          user!.email,
          () => null,
        ),
      );
      dispatch(userApi.util.invalidateTags(['User']));
      navigate(getDefaultDashboardRoute());
    } catch (e) {
      const error = ensureError(e);
      console.error('Error responding to invite:', error);
      toast.error('Failed to respond to invite', {
        description: error.message || UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setLoading(false);
    }
  }

  function handleSignOutAndRedirect() {
    handleLogout(false); // Don't redirect to home page
    navigate(`/invite/${token}`);
  }

  function handleIgnoreInvite() {
    dispatch(userApi.util.invalidateTags(['PendingInvite']));
    navigate(getDefaultDashboardRoute());
  }

  if (isLoading) {
    return (
      <InviteContainer>
        <DataLoader />
      </InviteContainer>
    );
  }

  if (!invite) {
    return null;
  }

  return (
    <InviteContainer>
      <Inner className="border rounded-lg px-8 py-10">
        <InitialsCircle userName={invite.organisationName} size={80} />

        <Title>
          You have been invited to join{' '}
          <strong>{invite.organisationName}</strong>
        </Title>

        {user?.email === invite.email ? (
          <ButtonContainer>
            <InviteeDetails>
              <span>Accepting as</span>
              <span className="font-semibold">{user.email}</span>
            </InviteeDetails>
            <Button
              className="w-full"
              onClick={() => handleInviteResponse(true)}
            >
              Accept
            </Button>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => handleInviteResponse(false)}
            >
              Decline
            </Button>
          </ButtonContainer>
        ) : (
          <ButtonContainer>
            <InviteeDetails>
              <span>To accept the invite, please sign in as</span>
              <span className="font-semibold">{invite.email}</span>
            </InviteeDetails>
            <Button className="w-full" onClick={handleSignOutAndRedirect}>
              Sign in
            </Button>
            <Button
              className="w-full"
              variant="outline"
              onClick={handleIgnoreInvite}
            >
              Ignore
            </Button>
          </ButtonContainer>
        )}
      </Inner>
    </InviteContainer>
  );
}

const InviteContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80%;
  gap: 18px;
  @media only screen and (min-width: 768px) {
    & {
      width: 50%;
    }
  }
  @media only screen and (min-width: 1200px) {
    & {
      width: 25%;
    }
  }
`;

const Title = styled.p`
  font-size: 20px;
  text-align: center;
  margin-bottom: 12px;
`;

const InviteeDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 4px;
  font-size: 14px;
  margin-bottom: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;
